// Reveal: a small tertiary button that shows and hides the content written inside it.
import { I80Element, define, h, nextId } from "../base.js";
import { icon } from "../icons.js";

/**
 * <i80-reveal text="Show details" text-when-open="Hide details">
 *   Everything the reader only sometimes needs.
 * </i80-reveal>
 *
 * The same markup as the Ember I80::Reveal (the disclosure primitive plus a button).
 * Fires i80-toggle with {open}; toggle() and the open property work from script.
 */
export class I80Reveal extends I80Element {
  static get observedAttributes() {
    return ["text", "text-when-open", "open", "described-by"];
  }

  setup() {
    let root = this.part("reveal");
    if (!root) {
      root = h("div", { "data-i80-part": "reveal" },
        h("div", { class: "i80-disclosure-primitive__toggle" },
          h("button", { type: "button", "data-i80-part": "button" })),
        h("div", { class: "i80-disclosure-primitive__content", "data-i80-part": "content" },
          h("div", { class: "i80-reveal__content i80-typography-body-200 i80-foreground-primary", "data-i80-part": "body" }, this.takeContent())));
      this.append(root);
    }
    this._root = root;
    this._button = root.querySelector('[data-i80-part="button"]');
    this._content = root.querySelector('[data-i80-part="content"]');
    this._body = root.querySelector('[data-i80-part="body"]');
    // A clone carries the original's id; every instance gets its own.
    this._content.id = nextId("content");
    this._button.addEventListener("click", () => this.toggle());
  }

  adopt() {
    this._body.append(...this.takeContent());
  }

  update() {
    const open = this.flag("open");
    this._root.className = "i80-disclosure-primitive i80-reveal";
    const b = this._button;
    b.className = [
      "i80-button", "i80-button--color-tertiary", "i80-button--size-medium",
      "i80-reveal__toggle-button", open && "i80-reveal__toggle-button--is-open",
    ].filter(Boolean).join(" ");
    b.setAttribute("aria-expanded", String(open));
    b.setAttribute("aria-controls", this._content.id);
    const describedBy = this.getAttribute("described-by");
    if (describedBy) b.setAttribute("aria-describedby", describedBy);
    else b.removeAttribute("aria-describedby");
    const label = (open ? this.getAttribute("text-when-open") : null) ?? this.getAttribute("text") ?? "Show more";
    b.replaceChildren(
      h("span", { class: "i80-button__icon" }, icon("chevron-down", { stretched: true })),
      h("span", { class: "i80-button__text" }, label),
    );
    this._body.hidden = !open;
  }

  get open() {
    return this.flag("open");
  }

  set open(value) {
    this.toggleAttribute("open", !!value);
  }

  toggle(force) {
    const open = force ?? !this.flag("open");
    this.toggleAttribute("open", open);
    this.dispatchEvent(new CustomEvent("i80-toggle", { bubbles: true, detail: { open } }));
  }
}

define("i80-reveal", I80Reveal);
